import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button } from '~/components/ui';
import { SelectSellerContactsStep } from '~/components/common/SelectSellerContactsStep';
import { useGetTradingPartnerOrganizationsForBuyer } from '~/api/organizations';
import { useSellerRecipients } from '~/components/common/useSellerRecipients';
import {
  useGetRequestForQuoteFullyPriced,
  useSubmitRequestForQuote,
} from '~/api/requestForQuote';
import { useAddRequestForQuoteToCart } from '~/api/order';
import { useSubmitOnce } from '~/hooks/useSubmitOnce';
import { toastGraphQLError } from '~/utils';
import type { SellerInventoryForBuyer } from '~/types/inventory';
import { FacilityPicker } from './FacilityPicker';
import { useRandomTallySellerContactsLabels } from './useRandomTallySellerContactsLabels';

type SelectSellersForRandomTallyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  platformProductId: string;
  shippingLocationId: string;
  excludeOrganizationIds: string[];
  requestForQuoteId: string;
};

export function SelectSellersForRandomTallyModal({
  isOpen,
  onClose,
  onSubmitted,
  platformProductId,
  shippingLocationId,
  excludeOrganizationIds,
  requestForQuoteId,
}: Readonly<SelectSellersForRandomTallyModalProps>) {
  const { t } = useTranslation('order-builder');

  const recipients = useSellerRecipients();
  const sellerContactsLabels = useRandomTallySellerContactsLabels();
  const { selectedOrganizationIds, selectedOrganizations, sellerInvitations } =
    recipients;

  const { data: requestForQuoteData } =
    useGetRequestForQuoteFullyPriced(requestForQuoteId);
  const { mutate: submitRequestForQuote, isPending: isSubmittingRequest } =
    useSubmitRequestForQuote();
  const { mutate: addRequestForQuoteToCart, isPending: isAddingToCart } =
    useAddRequestForQuoteToCart();
  const submitOnce = useSubmitOnce();
  const isSubmitting = isSubmittingRequest || isAddingToCart;

  const isFullyPriced =
    requestForQuoteData?.requestForQuote?.fullyPriced === true;

  const [selectedSeller] = selectedOrganizations;
  const selectedOrganizationId =
    selectedSeller === undefined ? undefined : selectedSeller.organization.id;
  const { data: selectedSellerData, isFetching: isSelectedSellerFetching } =
    useGetTradingPartnerOrganizationsForBuyer(
      0,
      1,
      {
        onlyOrganizationIds:
          selectedOrganizationId === undefined ? [] : [selectedOrganizationId],
      },
      undefined,
      { enabled: selectedOrganizationId !== undefined }
    );
  const selectedSellerOrganization =
    selectedSellerData === undefined
      ? undefined
      : selectedSellerData.tradingPartnerOrganizationsForBuyer.content.find(
          (organization) => organization.id === selectedOrganizationId
        );
  const selectedSellerFacilityCount =
    selectedSellerOrganization === undefined
      ? undefined
      : selectedSellerOrganization.facilities.length;

  let addToCartDisabledReason: string | undefined;
  if (selectedOrganizationIds.size !== 1) {
    addToCartDisabledReason = t(
      'order_builder.create_random_tally.add_to_cart_disabled_reason'
    );
  } else if (!isFullyPriced) {
    addToCartDisabledReason = t(
      'order_builder.create_random_tally.add_to_cart_unpriced_reason'
    );
  } else if (
    selectedSellerFacilityCount === undefined &&
    isSelectedSellerFetching
  ) {
    addToCartDisabledReason = t(
      'order_builder.create_random_tally.selected_seller_loading'
    );
  } else if (selectedSellerFacilityCount === undefined) {
    addToCartDisabledReason = t(
      'order_builder.create_random_tally.selected_seller_load_failed'
    );
  }
  const addToCartDisabled = !!addToCartDisabledReason || isSubmitting;

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleAddToCart = (inventory: SellerInventoryForBuyer) => {
    if (selectedSeller === undefined) {
      return;
    }
    const { contacts, primaryContactId } = selectedSeller;
    submitOnce((onSettled) => {
      addRequestForQuoteToCart(
        {
          requestForQuoteId,
          facilityId: inventory.facility.id,
          recipientUserIds: contacts.map((contact) => contact.id),
          ...(primaryContactId && { primaryContactUserId: primaryContactId }),
        },
        {
          onSuccess: () => {
            toast.success(
              t('order_builder.create_random_tally.add_to_cart_success')
            );
            onSubmitted();
          },
          onError: toastGraphQLError,
          onSettled,
        }
      );
    });
  };

  const handleSubmit = () => {
    if (selectedOrganizationIds.size === 0) {
      toast.error(
        t('order_builder.create_random_tally.at_least_one_seller_required')
      );
      return;
    }

    submitOnce((onSettled) => {
      submitRequestForQuote(
        { id: requestForQuoteId, sellerInvitations },
        {
          onSuccess: () => {
            toast.success(t('order_builder.create_random_tally.success'));
            onSubmitted();
          },
          onError: toastGraphQLError,
          onSettled,
        }
      );
    });
  };

  const facilityPickerBaseFilter = useMemo(
    () => ({ destinationLocationId: shippingLocationId }),
    [shippingLocationId]
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <div className="bg-neut-light rounded-lg shadow-xl max-w-7xl w-full h-[min(600px,90vh)] flex flex-col">
          <ModalHeader
            title={t('order_builder.create_random_tally.sellers_heading')}
            onClose={handleClose}
          />

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-4">
            <SelectSellerContactsStep
              recipients={recipients}
              isSubmitting={isSubmitting}
              labels={sellerContactsLabels}
              testIdPrefix="random-tally"
              excludeOrganizationIds={excludeOrganizationIds}
            />
          </div>

          <div className="px-6 py-4 border-t border-neut-border flex items-center justify-between gap-3 shrink-0">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('order_builder.create_random_tally.back')}
            </Button>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                width="auto"
                className="shrink-0"
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                <span className="whitespace-nowrap">
                  {t('order_builder.create_random_tally.request_quote')}
                </span>
              </Button>
              <FacilityPicker
                organizationId={selectedOrganizationId ?? ''}
                platformProductId={platformProductId}
                facilityCount={selectedSellerFacilityCount ?? 0}
                baseFilter={facilityPickerBaseFilter}
                disabled={addToCartDisabled}
                disabledReason={addToCartDisabledReason}
                accessibleLabel={t(
                  'order_builder.create_random_tally.add_to_cart'
                )}
                label={t('order_builder.create_random_tally.add_to_cart')}
                bypassPricedGate
                onSelectFacility={handleAddToCart}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
